import { cleanupStorageByPrefix, getLocalStorageUsage, isLocalStorageAvailable } from './storageUtils';

// Constants
const CLEANUP_INTERVAL = 1000 * 60 * 60; // Run every hour
const STORAGE_THRESHOLD = 4 * 1024 * 1024; // 4MB threshold (80% of 5MB)
const CLEANUP_PREFIXES = ['appIdea_', 'canvasSettings_', 'generationComplete_', 'appBuilderAuthState', 'onboardingAuthState'];
const RECENT_APP_EXCLUSION_TIME = 1000 * 60 * 30; // 30 minutes - don't clean newly created apps
const LAST_CLEANUP_KEY = '__last_storage_cleanup__';

/**
 * Checks and cleans up localStorage if needed:
 * 1. Removes expired items (older than 24 hours)
 * 2. Runs cleanup if storage usage exceeds threshold
 * 3. Cleans up by prefix, starting with oldest items
 */
export const checkAndCleanupStorage = (): void => {
    if (!isLocalStorageAvailable()) return;

    try {
        // Check when we last ran cleanup
        const lastCleanup = localStorage.getItem(LAST_CLEANUP_KEY);
        const now = Date.now();
        const shouldRun = !lastCleanup || (now - parseInt(lastCleanup, 10)) > CLEANUP_INTERVAL;

        if (!shouldRun) return;



        // IMPORTANT: Check for recent app creation
        // Don't clean up if we just created an app to avoid race conditions
        const recentAppIdParams = getRecentAppIdFromUrl();
        if (recentAppIdParams) {

            localStorage.setItem(LAST_CLEANUP_KEY, now.toString());
            return;
        }

        // Update last cleanup time
        localStorage.setItem(LAST_CLEANUP_KEY, now.toString());

        // Check storage usage
        const currentUsage = getLocalStorageUsage();
        const usagePercentage = (currentUsage / (5 * 1024 * 1024)) * 100;



        // If usage is below threshold, only clean very old items
        if (currentUsage < STORAGE_THRESHOLD) {
            // Clean up items older than 24 hours
            cleanupOldItems(24);
            return;
        }

        // If usage is high, do a more aggressive cleanup


        // First clean up items older than 6 hours
        cleanupOldItems(6);

        // If still above threshold, clean up all app-related items
        // except for any item related to a current app
        if (getLocalStorageUsage() > STORAGE_THRESHOLD) {
            CLEANUP_PREFIXES.forEach(prefix => {


                // Skip the current app's data if any
                const appId = getCurrentAppId();
                if (appId && prefix.startsWith('appIdea_')) {

                    cleanupStorageByPrefixExcept(prefix, `appIdea_${appId}`);
                } else {
                    cleanupStorageByPrefix(prefix);
                }
            });
        }
    } catch (error) {
        console.error('[Storage Cleanup] Error during cleanup:', error);
    }
};

/**
 * Clean up items older than specified hours
 */
const cleanupOldItems = (hours: number): void => {
    if (!isLocalStorageAvailable()) return;

    const maxAge = hours * 60 * 60 * 1000; // Convert to milliseconds
    const now = Date.now();

    // Find items with timestamps in their values
    const cleanupKeys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || key === LAST_CLEANUP_KEY) continue;

        // Only check app-related items
        if (!CLEANUP_PREFIXES.some(prefix => key.startsWith(prefix))) continue;

        try {
            // Try to get a timestamp from the item if it has one
            const value = localStorage.getItem(key);
            if (!value) continue;

            // Check if the key itself has a timestamp pattern (appIdea_123456789)
            const keyTimestamp = getTimestampFromKey(key);
            if (keyTimestamp && (now - keyTimestamp) > maxAge) {
                cleanupKeys.push(key);
                continue;
            }

            // Try to parse value as JSON to look for timestamps
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                // Check for common timestamp fields
                if (parsed.timestamp && (now - parsed.timestamp) > maxAge) {
                    cleanupKeys.push(key);
                    continue;
                }

                if (parsed.createdAt && (now - new Date(parsed.createdAt).getTime()) > maxAge) {
                    cleanupKeys.push(key);
                    continue;
                }
            }
        } catch (e) {
            // Not JSON or no timestamp, ignore
        }
    }

    // Remove the keys
    if (cleanupKeys.length > 0) {

        cleanupKeys.forEach(key => localStorage.removeItem(key));
    }
};

/**
 * Try to extract a timestamp from a key (e.g., "appIdea_1234567890")
 */
const getTimestampFromKey = (key: string): number | null => {
    const parts = key.split('_');
    const lastPart = parts[parts.length - 1];

    // Check if the last part looks like a timestamp (all digits)
    if (/^\d+$/.test(lastPart)) {
        const timestamp = parseInt(lastPart, 10);
        // Only accept reasonable timestamps (after 2020)
        if (timestamp > 1577836800000) { // Jan 1, 2020
            return timestamp;
        }
    }

    return null;
};

/**
 * Clean up by prefix but exclude a specific key
 */
const cleanupStorageByPrefixExcept = (prefix: string, excludeKey: string): void => {
    if (!isLocalStorageAvailable()) return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix) && key !== excludeKey) {
            keysToRemove.push(key);
        }
    }


    keysToRemove.forEach(key => localStorage.removeItem(key));
};

/**
 * Get the current app ID from the URL if present
 */
const getCurrentAppId = (): string | null => {
    if (typeof window === 'undefined') return null;

    try {
        const url = new URL(window.location.href);
        return url.searchParams.get('appId');
    } catch (e) {
        return null;
    }
};

/**
 * Check if there's a recent app creation in progress based on URL params
 */
const getRecentAppIdFromUrl = (): { appId: string, isKickstart: boolean } | null => {
    if (typeof window === 'undefined') return null;

    try {
        const url = new URL(window.location.href);
        const appId = url.searchParams.get('appId');
        const mode = url.searchParams.get('mode');

        if (appId && mode === 'kickstart') {
            return { appId, isKickstart: true };
        }

        return null;
    } catch (e) {
        return null;
    }
};

/**
 * Initialize the cleanup service
 */
export const initStorageCleanupService = (): void => {
    // if (typeof window === 'undefined') return; // Skip on server

    // // Run initial cleanup
    // setTimeout(() => {
    //     checkAndCleanupStorage();
    // }, 5000); // Wait 5 seconds after page load

    // // Set up interval for periodic cleanup
    // setInterval(checkAndCleanupStorage, CLEANUP_INTERVAL);

    // // Add event listener for low storage events
    // if (window.addEventListener) {
    //     window.addEventListener('storage', (e) => {
    //         // QuotaExceededError is not directly available here, but we can check for null
    //         if (e.storageArea === localStorage && e.newValue === null && e.oldValue !== null) {
    //             console.warn('[Storage Cleanup] Possible storage quota issue detected, running emergency cleanup');
    //             CLEANUP_PREFIXES.forEach(prefix => cleanupStorageByPrefix(prefix));
    //         }
    //     });
    // }
};

// Run the init function if in browser
if (typeof window !== 'undefined') {
    // initStorageCleanupService();
} 